import React, { Fragment, useEffect, useState } from "react";
import { Row, Col, Card, Badge, Dropdown, Modal, Button, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import PortalMenu from "../Common/PortalMenu";
import SignatureModal from "../Common/SignatureModal";
import MomEmailModal from "../Common/MomEmailModal";
import PermissionWrapper from "../PermissionWrapper";
import api from "../../../api";
import swal from "sweetalert";
import { useSelector } from "react-redux";

const OtherMomList = () => {
  const user = useSelector((state) => state.auth?.userDetails || {});
  const [moms, setMoms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewingPdf, setPreviewingPdf] = useState(false);

  const [selectedMOM, setSelectedMOM] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [showSignModal, setShowSignModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailMom, setEmailMom] = useState(null);
  const [approveId, setApproveId] = useState(null);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNext: false,
    hasPrev: false,
  });

  // ================= FETCH =================
  useEffect(() => {
    fetchMOMs();
  }, [pagination.currentPage, pagination.limit]);

  const fetchMOMs = async () => {
    try {
      setLoading(true);
      const res = await api.get(
        `/other-mom?page=${pagination.currentPage}&limit=${pagination.limit}&sortBy=createdAt&sortOrder=desc`
      );
      if (res.data.success) {
        setMoms(res.data.data);
        if (res.data.pagination) {
          const p = res.data.pagination;
          setPagination((prev) => ({
            ...prev,
            currentPage: p.currentPage,
            totalPages: p.totalPages,
            totalCount: p.totalCount,
            hasNext: p.hasNextPage,
            hasPrev: p.hasPrevPage,
          }));
        }
      }
    } catch {
      swal("Error", "Failed to load MOMs", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) =>
    setPagination((prev) => ({ ...prev, currentPage: page }));

  // ================= APPROVE =================
  const handleApprove = async (id) => {
    const confirm = await swal({
      title: "Approve MOM?",
      text: "Signature is required for approval.",
      icon: "info",
      buttons: ["Cancel", "Provide Signature"],
    });
    if (!confirm) return;
    setApproveId(id);
    setShowSignModal(true);
  };

  const handleSaveSignature = async (file) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("clientSignature", file);
      const res = await api.patch(`/other-mom/${approveId}/approve`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        swal("Approved!", "MOM approved successfully", "success");
        setShowSignModal(false);
        setApproveId(null);
        fetchMOMs();
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to approve", "error");
    } finally {
      setLoading(false);
    }
  };

  // ================= REJECT =================
  const handleReject = async (id) => {
    const remarks = prompt("Enter rejection reason:");
    if (!remarks) return;
    try {
      const res = await api.patch(`/other-mom/${id}/reject`, { remarks });
      if (res.data.success) {
        swal("Rejected!", "MOM rejected", "success");
        fetchMOMs();
      }
    } catch {
      swal("Error", "Failed to reject MOM", "error");
    }
  };

  // ================= PDF =================
  const handlePreviewPDF = async (id) => {
    try {
      setPreviewingPdf(true);
      const res = await api.get(`/other-mom/${id}/pdf`, { responseType: "blob" });
      window.open(
        window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })),
        "_blank"
      );
    } catch {
      swal("Error", "Failed to preview PDF", "error");
    } finally {
      setPreviewingPdf(false);
    }
  };

  const handleDownloadPDF = async (id) => {
    try {
      setPreviewingPdf(true);
      const res = await api.get(`/other-mom/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `MOM-${id}.pdf`;
      link.click();
    } catch {
      swal("Error", "Failed to download PDF", "error");
    } finally {
      setPreviewingPdf(false);
    }
  };

  const handleOpenEmail = (mom) => {
    setEmailMom(mom);
    setShowEmailModal(true);
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    const confirm = await swal({
      title: "Are you sure?",
      text: "This MOM will be deleted permanently!",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!confirm) return;
    try {
      const res = await api.delete(`/other-mom/${id}`);
      if (res.data.success) {
        swal("Deleted!", "MOM deleted successfully", "success");
        fetchMOMs();
      }
    } catch {
      swal("Error", "Failed to delete MOM", "error");
    }
  };

  // ================= VIEW =================
  const handleView = async (id) => {
    try {
      const res = await api.get(`/other-mom/${id}`);
      if (res.data.success) {
        setSelectedMOM(res.data.data);
        setShowModal(true);
      }
    } catch {
      swal("Error", "Failed to load MOM details", "error");
    }
  };

  // ================= HELPERS =================
  const formatDate = (date) => {
    if (!date) return "---";
    return new Date(date).toLocaleDateString("en-IN");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Approved": return "success";
      case "Pending":  return "warning";
      case "Rejected": return "danger";
      default:         return "secondary";
    }
  };

  const svgMenu = (
    <svg width="20px" height="20px" viewBox="0 0 24 24">
      <circle fill="#000" cx="5" cy="12" r="2" />
      <circle fill="#000" cx="12" cy="12" r="2" />
      <circle fill="#000" cx="19" cy="12" r="2" />
    </svg>
  );

  // ================= UI =================
  return (
    <Fragment>
      <PageTitle activeMenu="Other MOM List" motherMenu="MOM" />

      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title>Other MOM List</Card.Title>
              <PermissionWrapper user={user} required={["create:moms"]}>
                <Link to="/other-mom/add" className="btn btn-primary">
                  + Add Other MOM
                </Link>
              </PermissionWrapper>
            </Card.Header>

            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary"></div>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover style={{ tableLayout: "fixed", width: "100%" }}>
                    <colgroup>
                      <col style={{ width: "45px" }} />
                      <col style={{ width: "175px" }} />
                      <col style={{ width: "100px" }} />
                      <col style={{ width: "auto" }} />
                      <col style={{ width: "150px" }} />
                      <col style={{ width: "120px" }} />
                      <col style={{ width: "100px" }} />
                      <col style={{ width: "80px" }} />
                    </colgroup>
                    <thead>
                      <tr style={{ textAlign: "left" }}>
                        <th style={{ textAlign: "left" }}>#</th>
                        <th style={{ textAlign: "left" }}>MOM No</th>
                        <th style={{ textAlign: "left" }}>Date</th>
                        <th style={{ textAlign: "left" }}>Subject</th>
                        <th style={{ textAlign: "left" }}>Prepared By</th>
                        <th style={{ textAlign: "left" }}>Venue</th>
                        <th style={{ textAlign: "left" }}>Status</th>
                        <th style={{ textAlign: "left" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {moms.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center text-muted py-4">
                            No MOMs found
                          </td>
                        </tr>
                      ) : (
                        moms.map((mom, i) => (
                          <tr key={mom._id}>
                            <td style={{ textAlign: "left" }}>
                              {(pagination.currentPage - 1) * pagination.limit + (i + 1)}
                            </td>
                            <td style={{ wordBreak: "break-all", textAlign: "left" }}>{mom.momNumber || "—"}</td>
                            <td style={{ textAlign: "left" }}>{formatDate(mom.meetingDate)}</td>
                            <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>{mom.subject || "—"}</td>
                            <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>{mom.preparedBy}</td>
                            <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>{mom.venue || "—"}</td>
                            <td style={{ textAlign: "left" }}>
                              <Badge bg={getStatusBadge(mom.status)}>
                                {mom.status}
                              </Badge>
                            </td>
                            <td style={{ textAlign: "left" }}>
                              <Dropdown>
                                <Dropdown.Toggle variant="" as="div" className="btn i-false">
                                  {svgMenu}
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end" as={PortalMenu}>
                                  <PermissionWrapper user={user} required={["read:moms"]}>
                                    <Dropdown.Item onClick={() => handleView(mom._id)}>
                                      <i className="fas fa-eye me-2"></i>View
                                    </Dropdown.Item>
                                  </PermissionWrapper>

                                  <PermissionWrapper user={user} required={["edit:moms"]}>
                                    <Dropdown.Item as={Link} to={`/other-mom/edit/${mom._id}`}>
                                      <i className="fas fa-edit me-2"></i>Edit
                                    </Dropdown.Item>
                                  </PermissionWrapper>

                                  <Dropdown.Divider />

                                  {mom.status === "Pending" && (
                                    <>
                                      <PermissionWrapper user={user} required={["approve:moms"]}>
                                        <Dropdown.Item
                                          className="text-success"
                                          onClick={() => handleApprove(mom._id)}
                                        >
                                          <i className="fas fa-check me-2"></i>Approve
                                        </Dropdown.Item>
                                      </PermissionWrapper>

                                      <PermissionWrapper user={user} required={["reject:moms"]}>
                                        <Dropdown.Item
                                          className="text-danger"
                                          onClick={() => handleReject(mom._id)}
                                        >
                                          <i className="fas fa-times me-2"></i>Reject
                                        </Dropdown.Item>
                                      </PermissionWrapper>

                                      <Dropdown.Divider />
                                    </>
                                  )}

                                  <PermissionWrapper user={user} required={["read:moms"]}>
                                    <Dropdown.Item onClick={() => handlePreviewPDF(mom._id)}>
                                      <i className="fas fa-eye me-2 text-primary"></i>Preview PDF
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleDownloadPDF(mom._id)}>
                                      <i className="fas fa-file-pdf me-2 text-danger"></i>Download PDF
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleOpenEmail(mom)}>
                                      <i className="fas fa-envelope me-2 text-info"></i>Send Mail
                                    </Dropdown.Item>
                                  </PermissionWrapper>

                                  <Dropdown.Divider />

                                  <PermissionWrapper user={user} required={["delete:moms"]}>
                                    <Dropdown.Item
                                      className="text-danger"
                                      onClick={() => handleDelete(mom._id)}
                                    >
                                      <i className="fas fa-trash me-2"></i>Delete
                                    </Dropdown.Item>
                                  </PermissionWrapper>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>

                  <AppPagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalCount={pagination.totalCount}
                    itemsLength={moms.length}
                    limit={pagination.limit}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ===== VIEW MODAL ===== */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>MOM Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMOM && (
            <>
              <p><strong>MOM No:</strong> {selectedMOM.momNumber}</p>
              <p><strong>Date:</strong> {formatDate(selectedMOM.meetingDate)}</p>
              <p><strong>Subject:</strong> {selectedMOM.subject || "—"}</p>
              <p><strong>Prepared By:</strong> {selectedMOM.preparedBy}</p>
              <p><strong>Venue:</strong> {selectedMOM.venue}</p>
              <p><strong>Duration:</strong> {selectedMOM.duration}</p>
              <p>
                <strong>Time:</strong> {selectedMOM.fromTime} — {selectedMOM.toTime}
              </p>
              {selectedMOM.objective && (
                <p><strong>Objective:</strong> {selectedMOM.objective}</p>
              )}
              <hr />
              <h6>Minutes</h6>
              <ul>
                {selectedMOM.minutes?.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedMOM && (
            <>
              <Button
                variant="outline-primary"
                onClick={() => handlePreviewPDF(selectedMOM._id)}
              >
                Preview PDF
              </Button>
              <Button
                variant="danger"
                onClick={() => handleDownloadPDF(selectedMOM._id)}
              >
                Download PDF
              </Button>
            </>
          )}
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== PDF LOADING OVERLAY ===== */}
      {previewingPdf && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: "rgba(0,0,0,0.4)", zIndex: 9999 }}
        >
          <div className="bg-white p-4 rounded shadow text-center">
            <div className="spinner-border text-primary mb-3"></div>
            <h6 className="mb-0">Preparing MOM PDF…</h6>
          </div>
        </div>
      )}

      {/* ===== SIGNATURE MODAL ===== */}
      <SignatureModal
        show={showSignModal}
        onHide={() => { setShowSignModal(false); setApproveId(null); }}
        onSave={handleSaveSignature}
        title="Signature for MOM Approval"
      />

      {/* ===== EMAIL MODAL ===== */}
      <MomEmailModal
        show={showEmailModal}
        onHide={() => {
          setShowEmailModal(false);
          setEmailMom(null);
        }}
        mom={emailMom}
        type="other-mom"
      />
    </Fragment>
  );
};

export default OtherMomList;
